import React, { useCallback, useEffect, useState } from "react";

import Form from "react-bootstrap/Form";
import styled from "@emotion/styled";

import { Button } from "@/components/Buttons";
import Modal from "@/components/Modal";

import {
  COMMENT_MENU_TYPE,
  useDeliveryCommentsStore,
} from "@/stores/DeliveryComments";
import { useLoadingStore, useModalStore, useProfileStore } from "@/stores/hooks";

const Title = styled.h4`
  margin: 0;
`;

const SectionTitle = styled.div`
  font-weight: bold;
  margin-bottom: 10px;
`;

const CommentCard = styled.div`
  border: 1px solid #ddd;
  padding: 10px 12px;
  margin-bottom: 8px;
  background: ${({ mine }) => (mine ? "#f6faf7" : "#fcfcfb")};
`;

const TagLine = styled.div`
  font-size: 12px;
  color: #777;
  margin-bottom: 6px;
`;

const Tag = styled.span`
  display: inline-block;
  margin-right: 6px;
  padding: 1px 7px;
  background: ${({ mine }) => (mine ? "#eaf3ec" : "#fdecec")};
  color: ${({ mine }) => (mine ? "#3c8451" : "#b81c32")};
  font-weight: bold;
`;

const CommentText = styled.div`
  margin-bottom: 8px;
  line-height: 1.5;
  white-space: pre-wrap;
`;

const RowActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LeftActions = styled.div`
  display: flex;
  gap: 6px;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: #888;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    color: #ea2845;
  }
`;

const CheckState = styled.span`
  font-size: 12px;
  color: ${({ done }) => (done ? "#3c8451" : "#ea2845")};
`;

const LockedNote = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: #999;
`;

const Empty = styled.div`
  padding: 16px 0;
  color: #888;
`;

const WriteWrap = styled.div`
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #eee;
`;

const WriteFoot = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
`;

const Hint = styled.span`
  font-size: 12px;
  color: #888;
`;

const ButtonWrap = styled.div`
  margin-top: 16px;
  text-align: right;
`;

export default function CommentsModal({
  bookId,
  menuType = COMMENT_MENU_TYPE.CS,
}) {
  const { closeModal } = useModalStore();
  const { startLoading, finishLoading } = useLoadingStore();
  const { user } = useProfileStore().state;
  const { fetchUser } = useProfileStore();
  const { comments } = useDeliveryCommentsStore().state;
  const { fetchByBookId, create, update, remove, confirm } =
    useDeliveryCommentsStore();
  const [content, setContent] = useState("");
  const [editingId, setEditingId] = useState(null);
  const currentWriterName = user?.realname || user?.corpTitle || "";

  const loadComments = useCallback(async () => {
    try {
      startLoading();
      await fetchByBookId(bookId);
    } catch (e) {
      window.alert(`코멘트 조회에 실패했습니다.\n${e.message}`);
    }

    finishLoading();
  }, [bookId, fetchByBookId, finishLoading, startLoading]);

  useEffect(() => {
    if (!currentWriterName) {
      fetchUser().catch(() => {});
    }
  }, [currentWriterName, fetchUser]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const refresh = useCallback(async () => {
    await loadComments();
  }, [loadComments]);

  const handleCreateOrUpdate = useCallback(async () => {
    const nextContent = (content || "").trim();

    if (!nextContent) {
      window.alert("코멘트 내용을 입력해주세요.");
      return;
    }

    try {
      startLoading();

      if (editingId) {
        await update(bookId, editingId, nextContent);
        window.alert("코멘트가 수정되었습니다.");
      } else {
        await create(bookId, { content: nextContent, menuType });
        window.alert("코멘트가 등록되었습니다.");
      }

      setContent("");
      setEditingId(null);
      finishLoading();
      await refresh();
    } catch (e) {
      finishLoading();
      window.alert(
        `코멘트 ${editingId ? "수정" : "등록"}에 실패했습니다.\n${e.message}`,
      );
    }
  }, [
    bookId,
    menuType,
    content,
    create,
    editingId,
    finishLoading,
    refresh,
    startLoading,
    update,
  ]);

  const handleEdit = useCallback((comment) => {
    if (!comment.canMutate(currentWriterName)) {
      window.alert("확인 처리된 코멘트는 삭제 및 수정할 수 없습니다.");
      return;
    }

    setEditingId(comment.getCommentId());
    setContent(comment.getContent());
  }, [currentWriterName]);

  const handleDelete = useCallback(
    async (comment) => {
      if (!comment.canMutate(currentWriterName)) {
        window.alert("확인 처리된 코멘트는 삭제 및 수정할 수 없습니다.");
        return;
      }

      if (!window.confirm("이 코멘트를 삭제하시겠습니까?")) {
        return;
      }

      try {
        startLoading();
        await remove(bookId, comment.getCommentId());
        window.alert("코멘트가 삭제되었습니다.");
        finishLoading();
        await refresh();
      } catch (e) {
        finishLoading();
        window.alert(`코멘트 삭제에 실패했습니다.\n${e.message}`);
      }
    },
    [bookId, currentWriterName, finishLoading, refresh, remove, startLoading],
  );

  const handleConfirm = useCallback(
    async (comment) => {
      if (!comment.canConfirm()) {
        window.alert("관리자가 작성한 미확인 코멘트만 확인할 수 있습니다.");
        return;
      }
      try {
        startLoading();
        await confirm(bookId, comment.getCommentId());
        window.alert("코멘트를 확인 처리했습니다.");
        finishLoading();
        await refresh();
      } catch (e) {
        finishLoading();
        window.alert(`코멘트 확인에 실패했습니다.\n${e.message}`);
      }
    },
    [bookId, confirm, finishLoading, refresh, startLoading],
  );

  return (
    <Modal size="lg">
      <Modal.Header>
        <Title>코멘트 내역 · {bookId}</Title>
      </Modal.Header>
      <Modal.Body>
        <SectionTitle>코멘트 내역</SectionTitle>
        {comments.length === 0 ? (
          <Empty>작성된 코멘트가 없습니다.</Empty>
        ) : (
          comments.map((comment) => {
            const mine = comment.isCorpWriter(currentWriterName);
            const done = comment.getIsConfirmed();

            return (
              <CommentCard key={comment.getCommentId()} mine={mine}>
                <TagLine>
                  <Tag mine={mine}>{comment.getMenuType()}</Tag>
                  {comment.renderCreatedAt()}
                  {comment.getWriterName()
                    ? ` · ${comment.getWriterName()}${
                        comment.renderWriterSide()
                          ? `(${comment.renderWriterSide()})`
                          : ""
                      }`
                    : comment.renderWriterSide()
                      ? ` · ${comment.renderWriterSide()}`
                      : ""}
                  {comment.edited || comment.isEdited ? " · 수정됨" : ""}
                </TagLine>
                <CommentText>{comment.getContent()}</CommentText>
                <RowActions>
                  <LeftActions>
                    {comment.canMutate(currentWriterName) && (
                      <>
                        <LinkButton type="button" onClick={() => handleEdit(comment)}>
                          수정
                        </LinkButton>
                        <LinkButton type="button" onClick={() => handleDelete(comment)}>
                          삭제
                        </LinkButton>
                      </>
                    )}
                  </LeftActions>
                  {done ? (
                    <CheckState done>
                      코멘트확인완료
                      {comment.renderConfirmedAt()
                        ? ` ${comment.renderConfirmedAt()}`
                        : ""}
                      {comment.getConfirmedByName()
                        ? ` ${comment.getConfirmedByName()}`
                        : ""}
                    </CheckState>
                  ) : comment.canConfirm() ? (
                    <LinkButton type="button" onClick={() => handleConfirm(comment)}>
                      코멘트확인
                    </LinkButton>
                  ) : null}
                </RowActions>
                {done && (
                  <LockedNote>
                    확인 처리된 코멘트는 수정·삭제할 수 없습니다.
                  </LockedNote>
                )}
              </CommentCard>
            );
          })
        )}
        <WriteWrap>
          <Form.Control
            as="textarea"
            placeholder="코멘트를 입력하세요"
            rows={3}
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
          <WriteFoot>
            <Hint>{editingId ? "선택한 코멘트를 수정합니다." : ""}</Hint>
            <div>
              {editingId && (
                <Button
                  type="button"
                  variant="secondary"
                  width="80px"
                  style={{ marginRight: 6 }}
                  onClick={() => {
                    setEditingId(null);
                    setContent("");
                  }}
                >
                  취소
                </Button>
              )}
              <Button type="button" width="80px" onClick={handleCreateOrUpdate}>
                {editingId ? "수정" : "등록"}
              </Button>
            </div>
          </WriteFoot>
        </WriteWrap>
        <ButtonWrap>
          <Button type="button" variant="secondary" width="100px" onClick={closeModal}>
            닫기
          </Button>
        </ButtonWrap>
      </Modal.Body>
    </Modal>
  );
}
